import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerOverlay } from '@chakra-ui/react';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { ButtonToggle } from '@peacecover/component';
import { XmarkCircle } from '@peacecover/icon';

import useArtistEventsQuery from '../../../components/api/queries/useArtistEventsQuery';
import useUserQuery from '../../../components/api/queries/useUserQuery';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryLineButton from '../../../components/buttons/SecondaryLineButton';
import { EventNameBox } from '../../../components/event/EventNameBox';
import EventsGroupList from '../../../components/lists/EventsGroupList';
import { ListRenderer } from '../../../components/lists/ListRenderer';
import GridSkeletons from '../../../components/skeletons/GridSkeletons';

const PHOTOCARD_CATEGORIES = ['default', 'special', 'md', 'trading'];

const TradingPhotoCardFilterBottomDrawer = ({
  artistId,
  identityVerified = false,
  sameCountryOnly = false,
  eventIds = [],
  photoCardCategories = [],
  isOpen,
  onClose,
  focusRef,
  onConfirm
}) => {
  const [checkedSameCountryOnly, setCheckedSameCountryOnly] = useState(sameCountryOnly);
  const [checkedIdentityVerified, setCheckedIdentityVerified] = useState(identityVerified);
  const [selectedEventIds, setSelectedEventIds] = useState(eventIds);
  const [selectedEventCategoryName, setSelectedEventCategoryName] = useState('all');
  const [selectedPhotoCardCategories, setSelectedPhotoCardCategories] =
    useState(photoCardCategories);

  const user = useUserQuery();
  const { events, isLoading: isEventsLoading } = useArtistEventsQuery({
    artistId: artistId,
    photoCardCategories: selectedPhotoCardCategories,
    eventType: 'official'
  });

  const onSelectEvent = (eventId) => {
    setSelectedEventIds((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  const onSelectCategory = (category) => {
    setSelectedPhotoCardCategories((prev) =>
      prev.includes(category)
        ? prev.filter((prevCategory) => prevCategory !== category)
        : [...prev, category]
    );
  };

  const onSelectedEventCategoryName = (eventCategoryName) => {
    setSelectedEventCategoryName(eventCategoryName);
  };

  const initFilters = () => {
    setCheckedSameCountryOnly(false);
    setCheckedIdentityVerified(false);
    setSelectedPhotoCardCategories([]);
    setSelectedEventIds([]);
  };

  const handleConfirm = () => {
    onConfirm({
      sameCountryOnly: checkedSameCountryOnly,
      identityVerified: checkedIdentityVerified,
      photoCardCategories: selectedPhotoCardCategories,
      eventIds: selectedEventIds
    });
    onClose();
  };

  const filteredEvents = useMemo(() => {
    if (isEventsLoading) {
      return undefined;
    }
    const list = artistId ? (events ?? []) : [];
    if (selectedEventCategoryName === 'all') {
      return list;
    }
    return list.filter((event) => event.categoryName === selectedEventCategoryName);
  }, [artistId, events, isEventsLoading, selectedEventCategoryName]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" finalFocusRef={focusRef}>
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          maxWidth: 'calc(var(--chakra-sizes-xl))',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          left: '',
          right: ''
        }}
      >
        <DrawerBody
          padding={0}
          minHeight={'60vh'}
          height={'60vh'}
          maxHeight={'60vh'}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <div className="pt-[38px] mx-[24px] pb-[16px] border-b border-separator1 whitespace-pre-wrap text-[15px] leading-[18px] font-semibold text-text-main-lv2 flex-shrink-0">
            {t('TradingPhotoCardFilterBottomDrawer.title')}
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="px-6">
              <div className="flex items-center py-[18px] gap-[33px] border-b border-separator1">
                <div className="text-[15px] leading-[18px] font-semibold shrink-0 text-text-main-lv2">
                  {t('TradingPhotoCardFilterBottomDrawer.postTypeLabel')}
                </div>
                <div className="flex items-center gap-[7px]">
                  <button
                    type="button"
                    className={`inline-flex flex-row items-center gap-[4px] py-[6px] px-[12px] rounded-full border whitespace-nowrap cursor-pointer text-[13px] font-medium shrink-0 ${
                      checkedSameCountryOnly
                        ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark bg-secondary-light dark:bg-secondary-dark'
                        : 'border-label-light dark:border-label-dark text-on-disable'
                    }`}
                    onClick={() => setCheckedSameCountryOnly(!checkedSameCountryOnly)}
                  >
                    <ButtonToggle type="check" size="sm" checked={checkedSameCountryOnly} />
                    {t('TradingPhotoCardFilterBottomDrawer.sameCountryOnlyTitle')}
                  </button>
                  {user?.country === 'KR' && (
                    <button
                      type="button"
                      className={`inline-flex flex-row items-center gap-[4px] py-[6px] px-[12px] rounded-full border whitespace-nowrap cursor-pointer text-[13px] font-medium shrink-0 ${
                        checkedIdentityVerified
                          ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark bg-secondary-light dark:bg-secondary-dark'
                          : 'border-label-light dark:border-label-dark text-on-disable'
                      }`}
                      onClick={() => setCheckedIdentityVerified(!checkedIdentityVerified)}
                    >
                      <ButtonToggle type="check" size="sm" checked={checkedIdentityVerified} />
                      {t('TradingPhotoCardFilterBottomDrawer.identityVerifiedTitle')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6">
              <div className="flex items-center py-[18px] gap-[24px] border-b border-separator1">
                <div className="text-[15px] leading-[18px] font-semibold shrink-0 text-text-main-lv2">
                  {t('TradingPhotoCardFilterBottomDrawer.categoryTitle')}
                </div>
                <div className="flex flex-row items-center gap-2 overflow-x-auto flex-1 hidden-scroll py-[1px]">
                  {PHOTOCARD_CATEGORIES.map((category) => {
                    const isChecked = selectedPhotoCardCategories.includes(category);
                    return (
                      <button
                        type="button"
                        className={`inline-flex flex-row items-center gap-[4px] py-[6px] px-[12px] rounded-full border whitespace-nowrap cursor-pointer text-[13px] font-medium shrink-0 ${
                          isChecked
                            ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark bg-secondary-light dark:bg-secondary-dark'
                            : 'border-label-light dark:border-label-dark text-on-disable'
                        }`}
                        key={category}
                        onClick={() => onSelectCategory(category)}
                      >
                        {t(`PhotoCardFilterBottomDrawer.${category}Category`)}
                        {isChecked && <XmarkCircle size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-[24px] pt-[20px]">
              <div className="text-[15px] leading-[18px] font-semibold shrink-0 text-text-main-lv2">
                {t('TradingPhotoCardFilterBottomDrawer.eventTitle')}
              </div>
            </div>
            <div className="pt-[14px] pb-[16px]">
              <EventsGroupList
                events={events}
                isLoading={isEventsLoading}
                selectedEventCategoryName={selectedEventCategoryName}
                onSelectedEventCategory={(eventCategoryName) =>
                  onSelectedEventCategoryName(eventCategoryName)
                }
              />
            </div>
            <div className="px-6">
              <ListRenderer
                data={filteredEvents}
                emptyComponent={
                  <div className="mt-8 text-[15px] font-normal leading-[18px] text-center text-onDisabled-light dark:text-onDisabled-dark">
                    {t('PhotoCardFilterBottomDrawer.emptyCategory')}
                  </div>
                }
                skeletonComponent={
                  <GridSkeletons
                    gridSize={1}
                    skeletonSize={10}
                    skeletonHeight={'48px'}
                    marginBottom={'8px'}
                  />
                }
                renderList={(data) =>
                  data.map((event) => (
                    <button
                      key={event.eventId}
                      type="button"
                      onClick={() => onSelectEvent(event.eventId)}
                      className="flex items-center w-full py-3 border-b-[1px] border-separator-light-1 dark:border-separator-dark-1"
                    >
                      <div className="grow">
                        <EventNameBox
                          event={event}
                          isDisabled={!selectedEventIds.includes(event.eventId)}
                        />
                      </div>
                      <ButtonToggle
                        type="check"
                        size="lg"
                        checked={selectedEventIds.includes(event.eventId)}
                      />
                    </button>
                  ))
                }
              />
            </div>
          </div>
        </DrawerBody>
        <DrawerFooter
          paddingTop="10px"
          paddingBottom="calc(env(safe-area-inset-bottom) + 20px)"
          paddingX="20px"
          gap={'6px'}
        >
          <SecondaryLineButton
            text={t('PhotoCardFilterBottomDrawer.initButton')}
            style={{ padding: '16px', border: '0px', fontSize: '17px' }}
            onClickButton={() => initFilters()}
          />
          <PrimaryButton
            fontWeight={600}
            fontSize={'17px'}
            lineHeight={'20px'}
            padding={'16px 34px'}
            width={'100%'}
            onClick={handleConfirm}
            text={t('confirm')}
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default TradingPhotoCardFilterBottomDrawer;
